import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL, fmtDate } from "@/lib/format";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Trash2, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ExcelTools } from "@/components/ExcelTools";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const emptyForm = { name: "", contact: "", phone: "", email: "", address: "", tax_no: "", tax_office: "", note: "" };
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [params] = useSearchParams();
  const { isAdmin } = useAuth();

  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(() => { load(); if (params.get("new")) setOpen(true); }, []);

  const openNew = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name || "", contact: c.contact || "", phone: c.phone || "", email: c.email || "",
      address: c.address || "", tax_no: c.tax_no || "", tax_office: c.tax_office || "", note: c.note || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Firma adı gerekli");
    try {
      if (editId) {
        await api.put(`/customers/${editId}`, form);
        toast.success("Müşteri güncellendi");
      } else {
        await api.post("/customers", form);
        toast.success("Müşteri eklendi");
      }
      setOpen(false);
      setEditId(null);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(editId ? "Güncellenemedi" : "Eklenemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) return;
    await api.delete(`/customers/${id}`); toast.success("Silindi"); load();
  };

  const filtered = items.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const totalReceivable = filtered.reduce((s, c) => s + Math.max(c.balance, 0), 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Müşteriler</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} müşteri · Toplam alacak: <span className="num font-semibold text-primary">{fmtTL(totalReceivable)}</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <ExcelTools kind="customers" label="Müşteriler" onImported={load} />
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild><Button data-testid="new-customer-btn" onClick={openNew}><Plus size={16} className="mr-1" /> Yeni Müşteri</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-lg">
            <DialogHeader><DialogTitle>{editId ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Firma / Müşteri Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Yetkili Kişi</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="customer-contact-input" /></div>
                <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>E-posta</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" /></div>
                <div><Label>Vergi No</Label><Input value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} data-testid="customer-taxno-input" /></div>
              </div>
              <div><Label>Vergi Dairesi</Label><Input value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} data-testid="customer-taxoffice-input" /></div>
              <div><Label>Adres</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address-input" /></div>
              <div><Label>Notlar</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="customer-note-input" /></div>
              <Button type="submit" className="w-full" data-testid="customer-submit-btn">{editId ? "Değişiklikleri Kaydet" : "Kaydet"}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Müşteri ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="customer-search" />
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Firma</TableHead>
            <TableHead>Telefon</TableHead>
            <TableHead className="text-right">Toplam Alışveriş</TableHead>
            <TableHead className="text-right">Toplam Ödeme</TableHead>
            <TableHead className="text-right">Kalan Borç</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell>
                  <Link to={`/musteriler/${c.id}`} className="font-semibold hover:text-primary flex items-center gap-1">
                    {c.name} <ExternalLink size={12} />
                  </Link>
                  <div className="text-xs text-muted-foreground">{c.contact}</div>
                </TableCell>
                <TableCell className="text-sm">{c.phone || "-"}</TableCell>
                <TableCell className="text-right num">{fmtTL(c.total_sales)}</TableCell>
                <TableCell className="text-right num text-emerald-500">{fmtTL(c.total_paid)}</TableCell>
                <TableCell className={`text-right num font-bold ${c.balance > 0 ? "text-primary" : c.balance < 0 ? "text-emerald-500" : ""}`}>{fmtTL(c.balance)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`} title="Düzenle"><Pencil size={14} /></Button>}
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => del(c.id)} data-testid={`customer-delete-${c.id}`} title="Sil"><Trash2 size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Müşteri bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
